import { BoxSelection, BoxSelectionBoxSize, Checkbox, Text, TextButton } from '@wix/wix-ui-tpa/cssVars';
import React, { useEffect, useMemo, useRef } from 'react';
import { useConfig } from '../../../../contexts/config-context';
import { useFocusFieldEvent } from '../../../../contexts/events-context';
import { ForcedState, useForcedState } from '../../../../contexts/forced-state-context';
import { useTranslation } from '../../../../contexts/translation-context';
import { bulkSlotsSize, DataStatus } from '../../appointment-state';
import { Actions, Components } from '../../bi-types';
import { useAppointmentContext } from '../../context/appointment-context';
import { Loader } from '../../loader';
import { Notification } from '../../notification';
import { useAppointmentBi } from '../../utils/bi';
import { classes, st } from './slots.st.css';
import { formatTimeRange, getFormattedDateLabel, getSlotByKey, getSlotIndex, getSlotKey, withoutRejectedSlots } from './utils';
export const TimeSlots = ({ isReloadingSlots, fieldId })=>{
    const { appointmentFieldProps, appointmentState, setAppointmentState } = useAppointmentContext();
    const selectedSlot = appointmentFieldProps?.value;
    const { t } = useTranslation();
    const [selectedId, setSelectedId] = React.useState(()=>getSlotKey(selectedSlot));
    const [shouldFocus, setShouldFocus] = React.useState(false);
    const { isMobile, isComposer, language } = useConfig();
    const { availabilitySlots: allAvailabilitySlots, timezone = '', currentSlotBulkSize, availabilityDates } = appointmentState || {};
    const rejectedSlotStartDates = appointmentFieldProps?.rejectedSlotStartDates;
    const availabilitySlots = useMemo(()=>withoutRejectedSlots(allAvailabilitySlots, rejectedSlotStartDates ?? []), [
        allAvailabilitySlots,
        rejectedSlotStartDates
    ]);
    const bi = useAppointmentBi();
    const forcedState = useForcedState();
    const boxSelectionRef = useRef(undefined);
    const visibility = isReloadingSlots || appointmentState?.dataStatus === DataStatus.LOADING ? 'hidden' : 'visible';
    const firstSlotRef = useRef(null);
    useFocusFieldEvent(()=>{
        firstSlotRef.current?.focus();
    }, fieldId);
    useEffect(()=>{
        if (shouldFocus && currentSlotBulkSize) {
            boxSelectionRef.current?.focus(currentSlotBulkSize - 4);
        }
    }, [
        shouldFocus,
        boxSelectionRef,
        currentSlotBulkSize
    ]);
    useEffect(()=>{
        setSelectedId(getSlotKey(selectedSlot));
        if (availabilitySlots?.length && currentSlotBulkSize) {
            const key = getSlotKey(selectedSlot);
            const slotIndex = getSlotIndex(key, availabilitySlots);
            if (slotIndex && slotIndex + 1 > currentSlotBulkSize) {
                setAppointmentState({
                    currentSlotBulkSize: slotIndex + 1
                });
            }
        } else if (!availabilitySlots?.length && selectedSlot) {
            appointmentFieldProps?.onChange?.(undefined);
        }
        setShouldFocus(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedSlot,
        availabilitySlots,
        availabilityDates
    ]);
    const onSlotSelected = (slotId)=>{
        if (appointmentFieldProps?.onChange && availabilitySlots) {
            const newSelectedSlot = getSlotByKey(slotId, availabilitySlots);
            if (newSelectedSlot?.slot?.endDate && newSelectedSlot?.slot?.startDate) {
                bi?.report({
                    action: Actions.CLICK,
                    component: Components.SLOT,
                    startTime: new Date(newSelectedSlot.slot.startDate).valueOf(),
                    endTime: new Date(newSelectedSlot.slot.endDate).valueOf()
                });
                appointmentFieldProps?.onChange({
                    startDate: newSelectedSlot.slot.startDate,
                    endDate: newSelectedSlot.slot.endDate,
                    timeZone: timezone
                });
            }
        }
    };
    const isMoreSlotsToDisplay = ()=>{
        const bulkSize = appointmentState.currentSlotBulkSize || bulkSlotsSize;
        return !!(availabilitySlots?.length && bulkSize < availabilitySlots?.length);
    };
    const onShowMoreClicked = ()=>{
        if (isMoreSlotsToDisplay()) {
            bi?.report({
                action: Actions.CLICK,
                component: Components.SHOW_MORE
            });
            const bulkSize = appointmentState.currentSlotBulkSize || bulkSlotsSize;
            setShouldFocus(true);
            setAppointmentState({
                currentSlotBulkSize: bulkSize + bulkSlotsSize
            });
        }
    };
    const renderSlots = ()=>{
        if (!availabilitySlots?.length) {
            return /*#__PURE__*/ React.createElement(Notification, {
                message: t('form.appointment.slots-not-found.text')
            });
        }
        return availabilitySlots.slice(0, appointmentState.currentSlotBulkSize).map((slot, index)=>{
            if (!slot.slot || !slot.slot.startDate || !slot.slot.endDate) {
                return null;
            }
            const { startDate, endDate } = slot.slot;
            const timeRange = formatTimeRange({
                startDate,
                endDate,
                locale: language,
                timezone
            });
            const key = getSlotKey({
                startDate: slot?.slot?.startDate,
                endDate: slot?.slot?.endDate
            });
            return /*#__PURE__*/ React.createElement(BoxSelection.Option, {
                key: key,
                id: key,
                className: classes.boxSelectionOption,
                checked: selectedId === key,
                "aria-label": timeRange,
                ref: index === 0 ? firstSlotRef : undefined
            }, /*#__PURE__*/ React.createElement(Text, {
                className: classes.timeRange
            }, timeRange));
        });
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: st(classes.root, {
            isMobile,
            forcedHover: forcedState.includes(ForcedState.INPUT_FIELDS_HOVER),
            isComposer
        })
    }, isReloadingSlots && /*#__PURE__*/ React.createElement("div", {
        className: classes.verticalCenterLoader
    }, /*#__PURE__*/ React.createElement(Loader, null)), /*#__PURE__*/ React.createElement("div", {
        style: {
            visibility
        }
    }, /*#__PURE__*/ React.createElement(BoxSelection, {
        size: BoxSelectionBoxSize.small,
        className: classes.boxSelection,
        name: "hour-selection",
        label: getFormattedDateLabel({
            selectedDate: appointmentState.selectedDate,
            locale: language,
            timezone
        }),
        onChange: ({ id })=>onSlotSelected(id),
        ref: boxSelectionRef
    }, renderSlots()), !!isMoreSlotsToDisplay() && /*#__PURE__*/ React.createElement(TextButton, {
        className: classes.textButton,
        onClick: ()=>onShowMoreClicked()
    }, t('form.appointment.show-more-slots.text'))));
};
TimeSlots.includeCheckboxInBundle = Checkbox;

//# sourceMappingURL=index.js.map